import { OptionIdentifier } from '@app/business-rules/components/search-input/search-input.component';
import {
  Option,
  SelectOption,
} from '@app/shared/components/select/select.component';
import { EventEmitter } from '@angular/core';
import { FormArray } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Attribute, AttributeDataType } from '@dg/attributes-data-access';

import { Observable, of } from 'rxjs';
import { delay, switchMap } from 'rxjs/operators';
import { freeze } from 'immer';

import { PredicateFormGroup, OutcomeFormGroup } from '../../model/modal';
import { BusinessRuleFacade } from '@app/business-rules/facades';
import {
  BusinessRuleFormGroup,
  RuleTypes,
  BusinessRule,
  StaticAttributes,
  BusinessRuleOutcome,
} from '@app/business-rules/model';
import { BusinessRulesApiService } from '@app/business-rules/services';
import {
  buildPredicateFormGroup,
  buildOutcomeFormGroup,
  createRuleUpsertForm,
  getModalHeaderKey,
  getRuleNameKey,
  getRuleNamePlaceholder,
  getAddPredicateLabel,
  makeOutcomeOptions,
  makeAttributeOptions,
  attributeNameToType,
  validateRuleStatus,
  validateRulePredicates,
} from '@app/business-rules/utils';

export interface UpsertState {
  addPredicateLabel: string;
  form: BusinessRuleFormGroup;
  i18n: Record<any, string>;
  modalHeaderText: string;
  ruleNamePlaceholder: string;
  ruleNameLabel: string;
  updateTypeOptions: Observable<DropdownOption[]>;
  isSegment: boolean;
  attributes: Attribute[];
  attributeOptions: SelectOption[];
  currentRule?: BusinessRule;
}

export type NotifyCallback = (event: { fieldType: FieldAddedEvent }) => void;

export interface UpsertViewModelApi {
  addOutcome: () => void;
  addPredicate: () => void;
  onFieldAdded: (cb: NotifyCallback) => void;
  predicateLabel: (index: number) => string;
  removePredicate: (index: number) => void;
  removeOutcome: (index: number) => void;
  setComparisonOperator: (
    option: Option,
    predicateFormGroup: PredicateFormGroup
  ) => void;
  setComparisonValue: (
    option: Option,
    predicateFormGroup: PredicateFormGroup
  ) => void;
  setFieldName: (
    option: SelectOption,
    predicateFormGroup: PredicateFormGroup
  ) => void;
  setUpdateType: (option: Option, outcomeFormGroup: OutcomeFormGroup) => void;
  setOutcomeValue: (value: OptionIdentifier, outcome: OutcomeFormGroup) => void;
  removeOutcomeValue: (outcome: OutcomeFormGroup) => void;
  submit: (cb?: (rule: BusinessRule) => void) => Promise<void>;
  canAddMorePredicates: () => boolean;
  isDeleteRule: () => boolean;
  shouldShowOperatorAndValue: (predicate: PredicateFormGroup) => boolean;
  getTypesFromAttribute: (
    loadedAttributes: Attribute[],
    selectedAttribute: string,
    selectedOperator: string
  ) => SelectOption[];
  getAttributeType: (
    loadedAttributes: Attribute[],
    selectedAttributeName: string,
    selectedOperator: string
  ) => AttributeDataType;
  getAttributeValueMin: (
    loadedAttributes: Attribute[],
    selectedAttributeName: string,
    selectedOperator: string
  ) => number | null;
  getFollowingLabel: (
    loadedAttributes: Attribute[],
    selectedAttributeName: string,
    selectedOperator: string
  ) => string | false;
  setPredicateDate: (
    date: Date,
    predicateFormGroup: PredicateFormGroup
  ) => void;
  ruleHasPrivacyWarning: (outcomeFormGroups: OutcomeFormGroup[]) => boolean;
  ruleIsInvalid: (form: BusinessRuleFormGroup) => boolean;
}

export type UpsertViewModel = UpsertState & UpsertViewModelApi;

export const enum FieldAddedEvent {
  PREDICATE,
  OUTCOME,
}

export interface DropdownOption {
  name: string;
  value: string;
}

const I18N_KEYS = [
  'BusinessRules_RuleName',
  'BusinessRules_AttributeName',
  'BusinessRules_AttributeValue',
  'BusinessRules_RemoveCondition',
  'BusinessRules_Then',
  'BusinessRules_Value',
  'BusinessRules_RemoveAction',
  'BusinessRules_AddAction',
  'BusinessRules_If',
  'BusinessRules_And',
  'Core_FieldRequired',
  'Core_Save',
  'Core_AllFieldsRequired',
  'BusinessRules_EditRule',
  'BusinessRules_AddRule',
  'BusinessRules_Equals',
  'BusinessRules_DoesNotEqual',
  'BusinessRules_AddToGroup',
  'BusinessRules_ActionType',
  'BusinessRules_Qualifier',
  'BusinessRules_AddProviderLicense',
  'BusinessRules_AttributeInstructions',
  'BusinessRules_Error_DuplicateRuleName',
  'OrgInternalContentCtrl_AddToPathway',
  'BusinessRules_Operator',
  'BusinessRules_GreaterThan',
  'BusinessRules_GreaterOrEqual',
  'BusinessRules_LessThan',
  'BusinessRules_LesserOrEqual',
  'BusinessRules_Contains',
  'BusinessRules_PrivacyOutcomeWarning',
  'Core_NoResults',
  'BusinessRules_Condition',
  'Core_Days',
  'BusinessRules_BeyondDaysAgo',
  'BusinessRules_WithinDaysAgo',
  'BusinessRules_DaysAgo',
  'BusinessRules_RuleError_Description',
];

export function makeUpsertVM(
  facade: BusinessRuleFacade,
  translate: TranslateService,
  rulesApiService: BusinessRulesApiService,
  orgId: number,
  refreshPaginationOnSubmit: boolean,
  attributes: Attribute[],
  ruleId?: number
): UpsertViewModel {
  const i18n = translate.instant(I18N_KEYS);

  // ---- property initialization ---- //
  const announceFieldChanges = new EventEmitter<unknown>();
  const currentRule = facade.findRuleById(ruleId);
  const attributeOptions = makeAttributeOptions(
    attributes,
    translate,
    currentRule
  );
  const form = createRuleUpsertForm(
    facade.currentRuleType,
    facade.validateName.bind(facade),
    attributeOptions,
    currentRule
  );
  const isSegment = facade.currentRuleType === RuleTypes.Segment;

  const updateTypeOptions: Observable<DropdownOption[]> = rulesApiService
    .loadActionOptions(orgId)
    .pipe(
      switchMap((options) => {
        const outcomeOptions = makeOutcomeOptions(
          options,
          translate,
          currentRule
        );
        return of(outcomeOptions);
      })
    );

  // ---- text for fields / modal ---- //
  const modalHeaderText = translate.instant(
    getModalHeaderKey(currentRule, facade.currentRuleType)
  );
  const ruleNameLabel = translate.instant(
    getRuleNameKey(facade.currentRuleType)
  );
  const ruleNamePlaceholder = translate.instant(
    getRuleNamePlaceholder(facade.currentRuleType)
  );
  const addPredicateLabel = translate.instant(
    getAddPredicateLabel(facade.currentRuleType)
  );

  const allOperatorOptions = {
    '=': {
      name: i18n.BusinessRules_Equals,
      value: '=',
    },
    '!=': {
      name: i18n.BusinessRules_DoesNotEqual,
      value: '!=',
    },
    contains: {
      name: i18n.BusinessRules_Contains,
      value: 'contains',
    },
    '>': {
      name: i18n.BusinessRules_GreaterThan,
      value: '>',
    },
    '>=': {
      name: i18n.BusinessRules_GreaterOrEqual,
      value: '>=',
    },
    '<': {
      name: i18n.BusinessRules_LessThan,
      value: '<',
    },
    '<=': {
      name: i18n.BusinessRules_LesserOrEqual,
      value: '<=',
    },
    '<ago': {
      name: i18n.BusinessRules_WithinDaysAgo,
      value: '<ago',
    },
    '>ago': {
      name: i18n.BusinessRules_BeyondDaysAgo,
      value: '>ago',
    },
  };

  const operatorOptionsByType = {
    // These properties should match the values allowed in OrganizationAttribute.attributeDataType
    string: [
      allOperatorOptions['='],
      allOperatorOptions['!='],
      allOperatorOptions.contains,
    ],
    number: [
      allOperatorOptions['='],
      allOperatorOptions['!='],
      allOperatorOptions['>'],
      allOperatorOptions['>='],
      allOperatorOptions['<'],
      allOperatorOptions['<='],
    ],
    datetime: [
      allOperatorOptions['='],
      allOperatorOptions['!='],
      allOperatorOptions['>'],
      allOperatorOptions['>='],
      allOperatorOptions['<'],
      allOperatorOptions['<='],
      allOperatorOptions['<ago'],
      allOperatorOptions['>ago'],
    ],
    bool: [allOperatorOptions['=']],
  };

  const numericDateOperators = ['<ago', '>ago'];

  // ---- build api ---- //
  const addPredicate = () => {
    const predicates = form.get('predicates') as FormArray;
    predicates.push(buildPredicateFormGroup());
    announceFieldChanges.next({ fieldType: FieldAddedEvent.PREDICATE });
  };

  const removePredicate = (index: number) => {
    const predicates = form.get('predicates') as FormArray;
    predicates.removeAt(index);
    form.markAsTouched();
  };

  const addOutcome = () => {
    const outcomes = form.get('outcomes') as FormArray;
    outcomes.push(buildOutcomeFormGroup());
    announceFieldChanges.next({ fieldType: FieldAddedEvent.OUTCOME });
  };

  const removeOutcome = (index: number) => {
    const outcomes = form.get('outcomes') as FormArray;
    outcomes.removeAt(index);
    form.markAsTouched();
  };

  const setFieldName = (
    attribute: SelectOption,
    predicateFormGroup: PredicateFormGroup
  ) => {
    const fieldDisplayName = predicateFormGroup.get('fieldDisplayName');
    fieldDisplayName.patchValue(attribute.title);
    fieldDisplayName.markAsDirty();

    const fieldNameField = predicateFormGroup.get('fieldName');
    fieldNameField.patchValue(attribute.id);
    fieldNameField.markAsDirty();

    // clear out previously stored comparison value on attribute change
    predicateFormGroup.get('comparisonValue').patchValue(null);
    // If apply to new or all, force equals and true
    if (
      attribute.id === StaticAttributes.APPLY_TO_NEW_USERS ||
      attribute.id === StaticAttributes.APPLY_TO_ALL_USERS
    ) {
      predicateFormGroup.get('comparisonOperator').patchValue('=');
      predicateFormGroup.get('comparisonValue').patchValue('True');
    }
  };

  const setComparisonOperator = (
    option: Option,
    predicateFormGroup: PredicateFormGroup
  ) => {
    const comparisonField = predicateFormGroup.get('comparisonOperator');
    comparisonField.patchValue(option.value);
    comparisonField.markAsDirty();
  };

  const setComparisonValue = (
    option: Option,
    predicateFormGroup: PredicateFormGroup
  ) => {
    const valueField = predicateFormGroup.get('comparisonValue');
    valueField.patchValue(option.value);
    valueField.markAsDirty();
  };

  const setUpdateType = (
    option: Option,
    outcomeFormGroup: OutcomeFormGroup
  ) => {
    const outcomeField = outcomeFormGroup.get('updateTypeResourceId');
    outcomeField.patchValue(option.value);
    outcomeField.markAsDirty();

    // clear any previously selected outcome value
    removeOutcomeValue(outcomeFormGroup);
  };

  const submit = async (cb?: (rule: BusinessRule) => void) => {
    cb = cb || (() => {});

    return await facade
      .upsertRule(form.value, refreshPaginationOnSubmit, currentRule ?? null)
      .toPromise()
      .then(cb);
  };

  const predicateLabel = (index: number) => {
    return index === 0 ? i18n.BusinessRules_If : i18n.BusinessRules_And;
  };

  const onFieldAdded = (notify: NotifyCallback) => {
    const subscription = announceFieldChanges
      .pipe(delay(100))
      .subscribe(notify);
    return subscription.unsubscribe.bind(subscription);
  };

  const setOutcomeValue = (
    value: OptionIdentifier,
    outcomeFormGroup: OutcomeFormGroup
  ) => {
    const updateValueField = outcomeFormGroup.get('updateValue');
    const updateDisplayNameField = outcomeFormGroup.get('displayName');

    updateValueField.patchValue(value.id);
    updateDisplayNameField.patchValue(value.displayText);

    updateDisplayNameField.markAsDirty();
    updateValueField.markAsDirty();
  };

  const removeOutcomeValue = (outcomeFormGroup: OutcomeFormGroup) => {
    const outcomeField = outcomeFormGroup.get('updateValue');
    const updateDisplayNameField = outcomeFormGroup.get('displayName');

    outcomeField.patchValue(null);
    updateDisplayNameField.patchValue(null);

    outcomeField.markAsPristine();
    updateDisplayNameField.markAsPristine();
  };

  const canAddMorePredicates = () => {
    const predicates = form.get('predicates') as FormArray;
    const isApplyToAll = predicates.value.find((v) => {
      return v.fieldName === StaticAttributes.APPLY_TO_ALL_USERS;
    });
    return predicates?.length < 5 && !isApplyToAll;
  };

  const isDeleteRule = () => {
    return (
      currentRule?.outcomes.filter(
        (x) => x.updateTypeResourceId === 'DeleteUser'
      ).length > 0
    );
  };

  const shouldShowOperatorAndValue = (predicate: PredicateFormGroup) => {
    return !(
      predicate.value.fieldName === StaticAttributes.APPLY_TO_ALL_USERS ||
      predicate.value.fieldName === StaticAttributes.APPLY_TO_NEW_USERS
    );
  };

  const ruleHasPrivacyWarning = (outcomeFormGroups: OutcomeFormGroup[]) => {
    return (
      outcomeFormGroups.filter(
        (outcome) => outcome.value.updateTypeResourceId === 'SetProfilePrivacy'
      ).length > 0
    );
  };

  const getTypesFromAttribute = (
    loadedAttributes: Attribute[],
    selectedAttributeName: string,
    selectedOperator: string
  ) => {
    // Default to string if we don't have enough information
    if (!loadedAttributes || !selectedAttributeName) {
      return operatorOptionsByType['string'];
    }

    // If apply to all or new, just send equals
    if (
      selectedAttributeName === StaticAttributes.APPLY_TO_ALL_USERS ||
      selectedAttributeName === StaticAttributes.APPLY_TO_NEW_USERS
    ) {
      return [allOperatorOptions['=']];
    }

    const selectedAttribute: Attribute = loadedAttributes.find(
      (a) => a.attributeName === selectedAttributeName
    );

    // Default to string if for some reason there's no type
    const attributesList = !!selectedAttribute?.attributeDataType
      ? operatorOptionsByType[
          selectedAttribute.attributeDataType?.toLowerCase()
        ]
      : operatorOptionsByType['string'];

    /** If this operator has a pre-existing 'illegal' value,
     * go ahead and allow it.
     * Unless it also doesn't exist in the all operators list,
     *  in which case something's very wrong.
     */
    if (
      !attributesList.find((a) => a.value === selectedOperator) &&
      allOperatorOptions[selectedOperator]
    ) {
      return [allOperatorOptions[selectedOperator]].concat(attributesList);
    }

    // Otherwise, return curated list
    return attributesList;
  };

  const getAttributeType = (
    loadedAttributes: Attribute[],
    selectedAttributeName: string,
    selectedOperator: string
  ): AttributeDataType => {
    const baseAttributeType = attributeNameToType(
      loadedAttributes,
      selectedAttributeName
    );

    // If types is datetime but we're expecting a relative value...
    if (
      numericDateOperators.indexOf(selectedOperator) > -1 &&
      baseAttributeType === AttributeDataType.DateTime
    ) {
      return AttributeDataType.Number;
    }
    return baseAttributeType;
  };

  const getAttributeValueMin = (
    loadedAttributes: Attribute[],
    selectedAttributeName: string,
    selectedOperator: string
  ): number | null => {
    // If this is actually a date in disguise and we're using a positive integer restricted type...
    if (
      numericDateOperators.indexOf(selectedOperator) > -1 &&
      attributeNameToType(loadedAttributes, selectedAttributeName) ===
        AttributeDataType.DateTime
    ) {
      return 0;
    }
    // Otherwise, don't restrict
    return null;
  };

  const getFollowingLabel = (
    loadedAttributes: Attribute[],
    selectedAttributeName: string,
    selectedOperator: string
  ) => {
    const baseAttributeType = attributeNameToType(
      loadedAttributes,
      selectedAttributeName
    );

    if (
      numericDateOperators.indexOf(selectedOperator) > -1 &&
      baseAttributeType === AttributeDataType.DateTime
    ) {
      if (selectedOperator === '<ago') {
        return i18n.Core_Days;
      }
      return i18n.BusinessRules_DaysAgo;
    }
    return false;
  };

  const setPredicateDate = (
    date: Date,
    predicateFormGroup: PredicateFormGroup
  ) => {
    const comparisonValue = predicateFormGroup.get('comparisonValue');
    const day = date.getDate();
    const month =
      date.getMonth() > 8 ? date.getMonth() + 1 : '0' + (date.getMonth() + 1);
    const year = date.getFullYear();
    // date must be in MM/DD/YYYY format
    comparisonValue.patchValue(`${month}/${day}/${year}`);
  };

  const ruleIsInvalid = (form: BusinessRuleFormGroup) => {
    const outcomes = form.get('outcomes') as FormArray;
    const predicates = form.get('predicates') as FormArray;

    const mappedPredicates = predicates?.controls.map((predicate) => {
      return {
        comparisonValue: predicate.get('comparisonValue').value,
      };
    });

    // if this is a segment, we don't need to check outcomes
    if (isSegment) {
      return validateRulePredicates(mappedPredicates);
    }

    // If there are any warning or error statuses on the rule, the rule is invalid
    const mappedOutcomes = outcomes?.controls.map((outcome) => {
      return {
        updateTypeResourceId: outcome.get('updateTypeResourceId').value,
        updateValue: outcome.get('updateValue').value,
      };
    });
    return validateRuleStatus(mappedPredicates, mappedOutcomes).length > 0;
  };

  // view models are always immutable
  return freeze(
    {
      currentRule,
      addPredicateLabel,
      form,
      i18n,
      modalHeaderText,
      ruleNameLabel,
      ruleNamePlaceholder,
      updateTypeOptions,
      isSegment,
      attributes,
      attributeOptions,
      onFieldAdded,
      addPredicate,
      removePredicate,
      addOutcome,
      removeOutcome,
      setComparisonOperator,
      setComparisonValue,
      setUpdateType,
      submit,
      predicateLabel,
      setFieldName,
      setOutcomeValue,
      removeOutcomeValue,
      canAddMorePredicates,
      isDeleteRule,
      shouldShowOperatorAndValue,
      getTypesFromAttribute,
      getAttributeType,
      getAttributeValueMin,
      getFollowingLabel,
      setPredicateDate,
      ruleHasPrivacyWarning,
      ruleIsInvalid,
    },
    true
  ) as UpsertViewModel;
}
